import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
    FaBell,
    FaChartLine,
    FaDownload,
    FaEdit,
    FaEye,
    FaFileImport,
    FaFire,
    FaPlus,
    FaSearch,
    FaSyncAlt,
    FaTimesCircle,
    FaUsers
} from 'react-icons/fa';
import { getUserPrivileges, getUserRole, isAuthenticated } from '../../../utils/auth';
import api from '../../../utils/api';
import Sidebar from '../../../components/Sidebar/sidebar';
import Header from '../../../components/Header';
import './CorporateProspectManage.css';
import { API_BASE_URL } from '../../../utils/apiBase';
import { PROSPECT_PRIVILEGES } from '../../../utils/prospectPrivileges';

const DEFAULT_VISIBLE_COLUMNS = {
    select: true,
    prospectName: true,
    policyName: true,
    typeOfBusiness: true,
    premiumSize: true,
    firstFollowUpDate: true,
    source: true,
    owner: true,
    actions: true
};

const formatDisplayDate = date => {
    if (!date) return '-';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const getProspectStatus = (prospect, showDisabled) => {
    if (showDisabled || prospect?.isActive === false || prospect?.disabledReason) return 'Lost';
    if (prospect?.isConvertedToPolicy) return 'Converted';
    if (prospect?.firstFollowUpDate) return 'Contacted';
    return 'New';
};

const getFollowUpState = date => {
    if (!date) return '';
    const followUp = new Date(date);
    if (Number.isNaN(followUp.getTime())) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    followUp.setHours(0, 0, 0, 0);
    const daysAway = Math.ceil((followUp - today) / (1000 * 60 * 60 * 24));
    if (daysAway < 0) return 'overdue';
    if (daysAway <= 3) return 'soon';
    return 'scheduled';
};

const CorporateProspectManage = () => {
    const userRole = getUserRole();
    const privileges = getUserPrivileges();
    const isAdmin = userRole === 'admin';
    const corporatePrivileges = PROSPECT_PRIVILEGES.corporate;
    const hasPrivilege = (key) => isAdmin || privileges.includes(key);
    const canViewActive = hasPrivilege(corporatePrivileges.viewActive);
    const canManageLostProspects = hasPrivilege(corporatePrivileges.viewLost);
    const canImport = hasPrivilege(corporatePrivileges.import);
    const canExport = hasPrivilege(corporatePrivileges.export);
    const canEdit = hasPrivilege(corporatePrivileges.edit);
    const canConvert = hasPrivilege(corporatePrivileges.convert);
    const canMarkLost = hasPrivilege(corporatePrivileges.markLost);
    const canReactivate = hasPrivilege(corporatePrivileges.reactivate);
    const [collapsed, setCollapsed] = useState(false);
    const [prospects, setProspects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editData, setEditData] = useState(null);
    const [previousPolicies, setPreviousPolicies] = useState([]);
    const [disableId, setDisableId] = useState(null);
    const [disableReason, setDisableReason] = useState('');
    const [showDisabled, setShowDisabled] = useState(!canViewActive && canManageLostProspects);
    const [convertData, setConvertData] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [globalSearch, setGlobalSearch] = useState('');
    const deferredGlobalSearch = useDeferredValue(globalSearch);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [statusFilter, setStatusFilter] = useState('All Statuses');
    const [sourceFilter, setSourceFilter] = useState('All Sources');
    const [assignedFilter, setAssignedFilter] = useState('All Users');
    const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
    const [showColumnMenu, setShowColumnMenu] = useState(false);
    const [selectedProspectIds, setSelectedProspectIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [bulkLostModal, setBulkLostModal] = useState({ open: false, reason: '' });
    const [inlineEdit, setInlineEdit] = useState(null);
    const [editErrors, setEditErrors] = useState({ previousPolicyNumber: '' });
    const [premiumDisplay, setPremiumDisplay] = useState('');
    const [premiumError, setPremiumError] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const pageSize = 10;
    const tableWrapRef = useRef(null);
    const navigate = useNavigate();
    const safeSortConfig = sortConfig || { key: null, direction: 'asc' };
    const isEditFormInvalid = Boolean(editErrors.previousPolicyNumber);


    const formatDateForInput = date =>
        date ? new Date(date).toISOString().split('T')[0] : '';

    const loadProspects = useCallback(async () => {
        const url = showDisabled
            ? `${API_BASE_URL}/api/manage-prospects/corporate/disabled`
            : `${API_BASE_URL}/api/manage-prospects/corporate`;

        setLoading(true);
        try {
            const res = await api.get(url);
            setProspects(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                window.location.href = '/login';
            } else if (err.response?.status === 403) {
                navigate('/unauthorized');
            } else {
                console.error('Failed to load corporate prospects', err);
            }
        } finally {
            setLoading(false);
        }
    }, [navigate, showDisabled]);

    useEffect(() => {
        loadProspects();
        setSelectedProspectIds([]);
        setCurrentPage(1);
    }, [loadProspects, showDisabled]);

    useEffect(() => {
        if (!editData || editData.typeOfBusiness !== 'RENEWAL' || !editData.prospectName) {
            setPreviousPolicies([]);
            return;
        }

        const fetchPreviousPolicies = async () => {
            try {
                const res = await api.get(`${API_BASE_URL}/api/prospects/previous-policies`, {
                    params: { clientName: editData.prospectName }
                });
                setPreviousPolicies(res.data);
            } catch (err) {
                console.error('Failed to fetch previous policies', err);
                setPreviousPolicies([]);
            }
        };

        fetchPreviousPolicies();
    }, [editData]);

    const formatINR = value =>
        value ? new Intl.NumberFormat('en-IN').format(value) : '';

    const parseINR = value => value.replace(/[^0-9]/g, '');

    useEffect(() => {
        if (!editData?.premiumSize) return;
        setPremiumDisplay(formatINR(editData.premiumSize));
    }, [editData]);

    const validateEditPreviousPolicyNumber = (value, typeOfBusiness) => {
        if (typeOfBusiness !== 'ROLLOVER') return '';
        if (!value) return '';

        const allowedCharsRegex = /^[A-Z0-9/-]+$/i;
        if (!allowedCharsRegex.test(value)) return 'Only letters, numbers, / and - are allowed';
        if (value.length < 8) return 'Policy number is too short';
        if (value.length > 30) return 'Policy number seems too long';
        return '';
    };

    useEffect(() => {
        setEditErrors(prev => ({
            ...prev,
            previousPolicyNumber: ''
        }));
    }, [editData?.typeOfBusiness]);

    const handleUpdate = async () => {
        const payload = {
            premiumSize: editData.premiumSize,
            firstFollowUpDate: editData.firstFollowUpDate,
            remarks: editData.remarks
        };

        await api.put(`${API_BASE_URL}/api/manage-prospects/${editData._id}`, payload);
        setEditData(null);
        loadProspects();
    };

    const getSortableValue = (prospect, key) => {
        if (key === 'premiumSize') {
            const value = Number(prospect.premiumSize);
            return Number.isNaN(value) ? 0 : value;
        }

        if (key === 'firstFollowUpDate') {
            const value = prospect.firstFollowUpDate ? new Date(prospect.firstFollowUpDate).getTime() : 0;
            return Number.isNaN(value) ? 0 : value;
        }

        return String(prospect[key] || '').toLowerCase();
    };

    const sourceOptions = useMemo(() => {
        const values = prospects
            .map(p => p.source || p.sourceName || p.leadSource || '')
            .filter(Boolean);
        return ['All Sources', ...new Set(values)];
    }, [prospects]);

    const assignedOptions = useMemo(() => {
        const values = prospects
            .map(p => p.ownerId?.name || p.ownerName || p.assignedUser || '')
            .filter(Boolean);
        return ['All Users', ...new Set(values)];
    }, [prospects]);

    const filteredProspects = useMemo(() => {
        const search = deferredGlobalSearch.trim().toLowerCase();
        const fromTime = dateFrom ? new Date(dateFrom).getTime() : null;
        const toTime = dateTo ? new Date(dateTo).getTime() : null;

        return prospects.filter(prospect => {
            const searchableText = [
                prospect.prospectName,
                prospect.contactPersonName,
                prospect.policyName,
                prospect.typeOfBusiness,
                prospect.mobileNumber,
                prospect.phoneNumber,
                prospect.email,
                prospect.emailId,
                prospect.remarks,
                prospect.source,
                prospect.ownerId?.name
            ].join(' ').toLowerCase();

            const followUpTime = prospect.firstFollowUpDate
                ? new Date(prospect.firstFollowUpDate).getTime()
                : null;
            const prospectSource = prospect.source || prospect.sourceName || prospect.leadSource || '';
            const assignedUser = prospect.ownerId?.name || prospect.ownerName || prospect.assignedUser || '';
            const computedStatus = getProspectStatus(prospect, showDisabled);

            return (
                (!search || searchableText.includes(search)) &&
                (!fromTime || (followUpTime && followUpTime >= fromTime)) &&
                (!toTime || (followUpTime && followUpTime <= toTime)) &&
                (statusFilter === 'All Statuses' || computedStatus === statusFilter) &&
                (sourceFilter === 'All Sources' || prospectSource === sourceFilter) &&
                (assignedFilter === 'All Users' || assignedUser === assignedFilter)
            );
        });
    }, [prospects, deferredGlobalSearch, dateFrom, dateTo, statusFilter, sourceFilter, assignedFilter, showDisabled]);

    const sortedProspects = useMemo(() => [...filteredProspects].sort((a, b) => {
        if (!safeSortConfig.key) return 0;

        const valueA = getSortableValue(a, safeSortConfig.key);
        const valueB = getSortableValue(b, safeSortConfig.key);

        if (valueA < valueB) return safeSortConfig.direction === 'asc' ? -1 : 1;
        if (valueA > valueB) return safeSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    }), [filteredProspects, safeSortConfig.key, safeSortConfig.direction]);

    const totalPages = Math.max(1, Math.ceil(sortedProspects.length / pageSize));
    const paginatedProspects = useMemo(() => sortedProspects.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    ), [sortedProspects, currentPage]);

    const hotProspectCount = useMemo(
        () => prospects.filter(p => Number(p.premiumSize || 0) >= 100000).length,
        [prospects]
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [globalSearch, dateFrom, dateTo, statusFilter, sourceFilter, assignedFilter]);

    const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length + (showDisabled ? 1 : 0);

    const renderColGroup = () => (
        <colgroup>
            {visibleColumns.select && <col style={{ width: 62 }} />}
            {visibleColumns.prospectName && <col style={{ width: 300 }} />}
            {visibleColumns.policyName && <col style={{ width: 480 }} />}
            {visibleColumns.typeOfBusiness && <col style={{ width: 140 }} />}
            {visibleColumns.premiumSize && <col style={{ width: 170 }} />}
            {visibleColumns.firstFollowUpDate && <col style={{ width: 170 }} />}
            {visibleColumns.source && <col style={{ width: 150 }} />}
            {visibleColumns.owner && <col style={{ width: 150 }} />}
            {showDisabled && <col style={{ width: 220 }} />}
            {visibleColumns.actions && <col style={{ width: 210 }} />}
        </colgroup>
    );

    const toggleProspectSelection = id => {
        setSelectedProspectIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const toggleAllVisibleProspects = () => {
        const pageIds = paginatedProspects.map(p => p._id);
        const allSelected = pageIds.length > 0 && pageIds.every(id => selectedProspectIds.includes(id));

        setSelectedProspectIds(prev =>
            allSelected
                ? prev.filter(id => !pageIds.includes(id))
                : [...new Set([...prev, ...pageIds])]
        );
    };

    const exportProspects = (rows = sortedProspects) => {
        const selectedRows = selectedProspectIds.length
            ? rows.filter(row => selectedProspectIds.includes(row._id))
            : rows;

        if (!selectedRows.length) {
            alert('No prospects available to export');
            return;
        }

        const csvRows = [
            ['Company', 'Policy', 'Type', 'Premium', 'Follow-up', 'Status'],
            ...selectedRows.map(p => [
                p.prospectName || '',
                p.policyName || '',
                p.typeOfBusiness || '',
                p.premiumSize || '',
                formatDisplayDate(p.firstFollowUpDate),
                getProspectStatus(p, showDisabled)
            ])
        ];

        const csv = csvRows
            .map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'corporate-prospects.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleSort = columnKey => {
        setSortConfig(prev => {
            const current = prev || { key: null, direction: 'asc' };
            if (current.key === columnKey) {
                return { key: columnKey, direction: current.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key: columnKey, direction: 'asc' };
        });
    };

    const getSortIndicator = columnKey => {
        if (safeSortConfig.key !== columnKey) return '';
        return safeSortConfig.direction === 'asc' ? '▲' : '▼';
    };

    const openInlineEdit = (prospect, field) => {
        if (!canEdit || showDisabled) return;
        const currentValue = field === 'firstFollowUpDate'
            ? formatDateForInput(prospect.firstFollowUpDate)
            : (prospect[field] || '');
        setInlineEdit({ id: prospect._id, field, value: currentValue, saving: false, error: '' });
    };

    const cancelInlineEdit = () => setInlineEdit(null);

    const saveInlineEdit = async () => {
        if (!inlineEdit?.id || !inlineEdit?.field) return;
        const { id, field } = inlineEdit;

        setInlineEdit(prev => ({ ...prev, saving: true, error: '' }));

        const payload = field === 'firstFollowUpDate'
            ? { firstFollowUpDate: inlineEdit.value || null }
            : { [field]: inlineEdit.value };

        try {
            await api.put(`${API_BASE_URL}/api/manage-prospects/${id}`, payload);
            setInlineEdit(null);
            loadProspects();
        } catch (err) {
            const message = err?.response?.data?.error || 'Failed to save changes';
            setInlineEdit(prev => ({ ...prev, saving: false, error: message }));
        }
    };

    const resetProspectFilters = () => {
        setGlobalSearch('');
        setDateFrom('');
        setDateTo('');
        setStatusFilter('All Statuses');
        setSourceFilter('All Sources');
        setAssignedFilter('All Users');
    };

    const renderTableHeader = () => (
        <tr className="retail-prospects-table-head-row">
            {visibleColumns.select && (
                <th className="select-col retail-prospects-table-th retail-prospects-table-th-select">
                    <input
                        type="checkbox"
                        checked={paginatedProspects.length > 0 && paginatedProspects.every(p => selectedProspectIds.includes(p._id))}
                        onChange={toggleAllVisibleProspects}
                    />
                </th>
            )}
            {visibleColumns.prospectName && (
                <th className="retail-prospects-table-th retail-prospects-table-th-name" data-col="name" onClick={() => handleSort('prospectName')}>
                    <div className="th-inner">
                        <span>Company {getSortIndicator('prospectName')}</span>
                    </div>
                </th>
            )}
            {visibleColumns.policyName && (
                <th className="retail-prospects-table-th" onClick={() => handleSort('policyName')}>
                    <div className="th-inner">
                        <span>Policy {getSortIndicator('policyName')}</span>
                    </div>
                </th>
            )}
            {visibleColumns.typeOfBusiness && (
                <th className="retail-prospects-table-th" onClick={() => handleSort('typeOfBusiness')}>
                    <div className="th-inner">
                        <span>Type {getSortIndicator('typeOfBusiness')}</span>
                    </div>
                </th>
            )}
            {visibleColumns.premiumSize && (
                <th className="retail-prospects-table-th" onClick={() => handleSort('premiumSize')}>
                    <div className="th-inner">
                        <span>Premium {getSortIndicator('premiumSize')}</span>
                    </div>
                </th>
            )}
            {visibleColumns.firstFollowUpDate && (
                <th className="retail-prospects-table-th" onClick={() => handleSort('firstFollowUpDate')}>
                    <div className="th-inner">
                        <span>Follow-up {getSortIndicator('firstFollowUpDate')}</span>
                    </div>
                </th>
            )}
            {visibleColumns.source && (
                <th className="retail-prospects-table-th">
                    <div className="th-inner">
                        <span>Source</span>
                    </div>
                </th>
            )}
            {visibleColumns.owner && (
                <th className="retail-prospects-table-th">
                    <div className="th-inner">
                        <span>Assigned</span>
                    </div>
                </th>
            )}
            {showDisabled && <th className="retail-prospects-table-th">Lost Reason</th>}
            {visibleColumns.actions && <th className="actions-col retail-prospects-table-th retail-prospects-table-th-actions">Actions</th>}
        </tr>
    );

    if (!isAuthenticated()) {
        return <Navigate to="/" />;
    }

    return (
        <div className="retail-policy-manage-prospect-container">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>
            <div className={`retail-policy-manage-prospect-content ${collapsed ? 'collapsed' : ''}`}>
                <div className="retail-policy-manage-prospect-sticky-header">
                    <Header />
                </div>

                <div className="retail-policy-manage-prospect-scroll-area">
                    <section className="retail-prospect-shell policy-table-section">
                        <div className="retail-prospect-hero converted-policy-page-hero">
                            <div>
                                <span className="retail-prospect-eyebrow converted-policy-eyebrow">Transactions / Manage Prospects / Corporate</span>
                                <h1>Corporate Prospects</h1>
                                <p>{sortedProspects.length} of {prospects.length} records visible</p>
                            </div>

                            <div className="retail-prospect-quick-actions">
                                <button className="converted-policy-reset-btn" type="button" onClick={resetProspectFilters}>
                                    Reset filters
                                </button>
                                <button type="button" onClick={() => navigate('/prospect/retail-prospect')} title="Add Prospect">
                                    <FaPlus /> Add Prospect
                                </button>
                                {canImport && <button type="button" onClick={() => alert('Import flow is not configured yet')} title="Import Prospects">
                                    <FaFileImport /> Import
                                </button>}
                                {canExport && <button type="button" onClick={() => exportProspects()} title="Export current prospects">
                                    <FaDownload /> Export
                                </button>}
                            </div>
                        </div>

                        <div className="prospect-summary-grid converted-policy-summary-grid">
                            <div className="prospect-summary-card converted-policy-summary-card blue">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FaUsers style={{ color: '#1d4ed8', fontSize: '1.3rem' }} />
                                    <span>Total</span>
                                </div>
                                <strong>{prospects.length}</strong>
                                <small>{showDisabled ? 'Lost prospects' : 'Active prospects'}</small>
                            </div>
                            <div className="prospect-summary-card converted-policy-summary-card amber hot">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FaFire style={{ color: '#b45309', fontSize: '1.3rem' }} />
                                    <span>Hot</span>
                                </div>
                                <strong>{hotProspectCount}</strong>
                                <small>Premium ≥ ₹1,00,000</small>
                            </div>
                            <div className="prospect-summary-card converted-policy-summary-card green">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FaChartLine style={{ color: '#059669', fontSize: '1.3rem' }} />
                                    <span>Visible</span>
                                </div>
                                <strong>{filteredProspects.length}</strong>
                                <small>After filters</small>
                            </div>
                            <div className="prospect-summary-card converted-policy-summary-card violet">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FaBell style={{ color: '#7c3aed', fontSize: '1.3rem' }} />
                                    <span>Selected</span>
                                </div>
                                <strong>{selectedProspectIds.length}</strong>
                                <small>Bulk actions ready</small>
                            </div>
                        </div>

                        <div className="retail-prospect-controls-card converted-policy-filter-panel">
                            <label className="prospect-global-search converted-policy-search">
                                <FaSearch />
                                <input
                                    value={globalSearch}
                                    onChange={e => setGlobalSearch(e.target.value)}
                                    placeholder="Search company, policy, email, remarks..."
                                />
                            </label>

                            <div className="prospect-filter-grid converted-policy-table-controls">
                                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                    {['All Statuses', 'New', 'Contacted', 'Converted', 'Lost'].map(option => (
                                        <option key={option}>{option}</option>
                                    ))}
                                </select>
                                <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
                                    {sourceOptions.map(option => <option key={option}>{option}</option>)}
                                </select>
                                <select value={assignedFilter} onChange={e => setAssignedFilter(e.target.value)}>
                                    {assignedOptions.map(option => <option key={option}>{option}</option>)}
                                </select>
                            </div>

                            <div className="retail-prospect-toolbar">
                                {canViewActive && <button className={`prospect-filter-btn ${!showDisabled ? 'active' : ''}`} onClick={() => setShowDisabled(false)}>
                                    Active Prospects
                                </button>}

                                {canManageLostProspects && (
                                    <button className={`prospect-filter-btn prospect-filter-btn-warning ${showDisabled ? 'active' : ''}`} onClick={() => setShowDisabled(true)}>
                                        Lost Prospects
                                    </button>
                                )}

                                <button type="button" className="prospect-filter-btn" onClick={loadProspects}>
                                    <FaSyncAlt /> Refresh
                                </button>

                                <div className="column-toggle-wrap">
                                    <button type="button" className="prospect-filter-btn" onClick={() => setShowColumnMenu(prev => !prev)}>
                                        Columns
                                    </button>
                                    {showColumnMenu && (
                                        <div className="column-toggle-menu">
                                            {Object.keys(DEFAULT_VISIBLE_COLUMNS).map(columnKey => (
                                                <label key={columnKey}>
                                                    <input
                                                        type="checkbox"
                                                        checked={visibleColumns[columnKey]}
                                                        onChange={() => setVisibleColumns(prev => ({
                                                            ...prev,
                                                            [columnKey]: !prev[columnKey]
                                                        }))}
                                                    />
                                                    {columnKey === 'select' ? 'Select' : columnKey.replace(/([A-Z])/g, ' $1')}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {selectedProspectIds.length > 0 && (
                                    <>
                                        {canExport && <button type="button" className="prospect-filter-btn prospect-filter-btn-primary" onClick={() => exportProspects()}>
                                            Export Selected ({selectedProspectIds.length})
                                        </button>}
                                        {canMarkLost && !showDisabled && (
                                            <button
                                                type="button"
                                                className="prospect-filter-btn prospect-filter-btn-warning"
                                                onClick={() => setBulkLostModal({ open: true, reason: '' })}
                                            >
                                                Mark Lost (Bulk)
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="prospect-active-filter-chips converted-policy-filter-chips" aria-label="Active prospect filters">
                                <span>{showDisabled ? 'Lost pipeline' : 'Active pipeline'}</span>
                                {statusFilter !== 'All Statuses' && <span>Status: {statusFilter}</span>}
                                {sourceFilter !== 'All Sources' && <span>Source: {sourceFilter}</span>}
                                {assignedFilter !== 'All Users' && <span>Assigned: {assignedFilter}</span>}
                                {(dateFrom || dateTo) && <span>Follow-up window</span>}
                                {globalSearch.trim() && <span>Search active</span>}
                            </div>
                        </div>

                        {selectedProspectIds.length > 0 && (
                            <div className="converted-policy-bulkbar">
                                <strong>{selectedProspectIds.length} selected</strong>
                                <span>Use row actions to preview, edit, convert, mark lost, export, or reactivate prospects.</span>
                            </div>
                        )}

                        <div className="prospect-table-card retail-prospect-table-scope retail-prospects-table-component">
                            <div className="prospect-table-card-head">
                                <div>
                                    <span>Corporate prospect list</span>
                                    <strong>{sortedProspects.length} records</strong>
                                </div>
                                
                            </div>
                            <div className="prospect-table-wrap retail-prospects-table-scroll converted-policy-table-scroll" ref={tableWrapRef}>
                                <table className="prospect-table retail-prospects-table converted-policy-table">
                                    {renderColGroup()}
                                    <thead>{renderTableHeader()}</thead>
                                    <tbody>
                                        {loading ? (
                                            Array.from({ length: 5 }).map((_, index) => (
                                                <tr key={index} className="prospect-skeleton-row retail-prospects-table-row">
                                                    <td className="retail-prospects-table-td" colSpan={visibleColumnCount}>
                                                        <span />
                                                    </td>
                                                </tr>
                                            ))
                                        ) : paginatedProspects.length ? (
                                            paginatedProspects.map(p => {
                                                const status = getProspectStatus(p, showDisabled);
                                                const isHot = Number(p.premiumSize || 0) >= 100000;
                                                const followUpState = getFollowUpState(p.firstFollowUpDate);

                                                return (
                                                    <tr
                                                        key={p._id}
                                                        className={[
                                                            'retail-prospects-table-row',
                                                            isHot ? 'hot-prospect-row' : '',
                                                            followUpState === 'overdue' ? 'followup-overdue-row' : ''
                                                        ].filter(Boolean).join(' ')}
                                                    >
                                                        {visibleColumns.select && (
                                                            <td className="select-col retail-prospects-table-td retail-prospects-table-td-select">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedProspectIds.includes(p._id)}
                                                                    onChange={() => toggleProspectSelection(p._id)}
                                                                />
                                                            </td>
                                                        )}
                                                        {visibleColumns.prospectName && (
                                                            <td className="retail-prospects-table-td retail-prospects-table-td-name" data-label="Company">
                                                                <div className="prospect-person-cell">
                                                                    <span className="prospect-avatar">{(p.prospectName || '?').charAt(0).toUpperCase()}</span>
                                                                    <div>
                                                                        <strong>{p.prospectName || '-'}</strong>
                                                                        <span className={`prospect-status-badge status-${status.toLowerCase()}`}>{status}</span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        )}
                                                        {visibleColumns.policyName && <td className="retail-prospects-table-td" data-label="Policy">{p.policyName || '-'}</td>}
                                                        {visibleColumns.typeOfBusiness && <td className="retail-prospects-table-td" data-label="Type">{p.typeOfBusiness || '-'}</td>}
                                                        {visibleColumns.premiumSize && (
                                                            <td className="retail-prospects-table-td" data-label="Premium">
                                                                <strong className={isHot ? 'hot-premium' : ''}>₹{formatINR(p.premiumSize)}</strong>
                                                            </td>
                                                        )}
                                                        {visibleColumns.firstFollowUpDate && (
                                                            <td
                                                                className={`retail-prospects-table-td ${canEdit && !showDisabled ? 'editable-cell' : ''}`}
                                                                data-label="Follow-up"
                                                                onDoubleClick={() => openInlineEdit(p, 'firstFollowUpDate')}
                                                            >
                                                                {inlineEdit?.id === p._id && inlineEdit.field === 'firstFollowUpDate' ? (
                                                                    <div className="inline-edit-wrap" onClick={e => e.stopPropagation()}>
                                                                        <input
                                                                            type="date"
                                                                            value={inlineEdit.value}
                                                                            onChange={e => setInlineEdit(prev => ({ ...prev, value: e.target.value }))}
                                                                            disabled={inlineEdit.saving}
                                                                        />
                                                                        <div className="inline-edit-actions">
                                                                            <button type="button" onClick={saveInlineEdit} disabled={inlineEdit.saving}>Save</button>
                                                                            <button type="button" onClick={cancelInlineEdit} disabled={inlineEdit.saving}>Cancel</button>
                                                                        </div>
                                                                        {inlineEdit.error && <small className="field-error">{inlineEdit.error}</small>}
                                                                    </div>
                                                                ) : (
                                                                    <span className={`followup-pill ${followUpState ? `followup-${followUpState}` : ''}`}>
                                                                        {formatDisplayDate(p.firstFollowUpDate)}
                                                                    </span>
                                                                )}
                                                            </td>
                                                        )}
                                                        {visibleColumns.source && <td className="retail-prospects-table-td" data-label="Source">{p.source || p.sourceName || p.leadSource || '-'}</td>}
                                                        {visibleColumns.owner && <td className="retail-prospects-table-td" data-label="Assigned">{p.ownerId?.name || p.ownerName || '-'}</td>}
                                                        {showDisabled && (
                                                            <td data-label="Lost Reason" className="retail-prospects-table-td prospect-lost-reason">
                                                                {p.disabledReason || '-'}
                                                            </td>
                                                        )}
                                                        {visibleColumns.actions && (
                                                            <td data-label="Actions" className="actions-col retail-prospects-table-td retail-prospects-table-td-actions">
                                                                <div className="prospect-action-group converted-policy-actions">
                                                                    <button className="icon-action view" title="Preview" data-tooltip="Preview" onClick={() => setPreviewData(p)}>
                                                                        <FaEye />
                                                                    </button>
                                                                    {!showDisabled ? (
                                                                        <>
                                                                            {canEdit && (
                                                                                <button className="icon-action edit" title="Edit" data-tooltip="Edit" onClick={() => setEditData(p)}>
                                                                                    <FaEdit />
                                                                                </button>
                                                                            )}
                                                                            {canConvert && (
                                                                                <button className="icon-action convert" title="Convert" data-tooltip="Convert" onClick={() => setConvertData(p)}>
                                                                                    <FaSyncAlt />
                                                                                </button>
                                                                            )}
                                                                            {canMarkLost && (
                                                                                <button
                                                                                    className="icon-action danger"
                                                                                    title="Mark as Lost"
                                                                                    data-tooltip="Mark Lost"
                                                                                    onClick={() => {
                                                                                        setDisableId(p._id);
                                                                                        setDisableReason('');
                                                                                    }}
                                                                                >
                                                                                    <FaTimesCircle />
                                                                                </button>
                                                                            )}
                                                                        </>
                                                                    ) : canReactivate ? (
                                                                        <button
                                                                            className="icon-action convert"
                                                                            title="Activate"
                                                                            data-tooltip="Activate"
                                                                            onClick={async () => {
                                                                                await api.put(`${API_BASE_URL}/api/manage-prospects/activate/${p._id}`);
                                                                                loadProspects();
                                                                            }}
                                                                        >
                                                                            <FaSyncAlt />
                                                                        </button>
                                                                    ) : null}
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr className="retail-prospects-table-row">
                                                <td colSpan={visibleColumnCount} className="retail-prospects-table-td prospect-empty-state">
                                                    <strong>No corporate prospects found</strong>
                                                    <span>Try changing filters or add a new prospect to begin.</span>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="prospect-pagination converted-policy-pagination-row">
                                <span>
                                    Page {currentPage} of {totalPages} · {sortedProspects.length} records
                                </span>
                                <div>
                                    <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}>
                                        Previous
                                    </button>
                                    <button type="button" disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}>
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>

                        {previewData && (
                            <div className="prospect-preview-overlay" onClick={() => setPreviewData(null)}>
                                <aside className="prospect-preview-drawer" onClick={e => e.stopPropagation()}>
                                    <div className="preview-drawer-head">
                                        <div>
                                            <span>Quick Preview</span>
                                            <h3>{previewData.prospectName || '-'}</h3>
                                        </div>
                                        <button type="button" onClick={() => setPreviewData(null)}>×</button>
                                    </div>

                                    <div className="preview-detail-grid">
                                        <p><strong>Company:</strong> {previewData.prospectName || '-'}</p>
                                        <p><strong>Contact Person:</strong> {previewData.contactPersonName || '-'}</p>
                                        <p><strong>Policy:</strong> {previewData.policyName || '-'}</p>
                                        <p><strong>Type:</strong> {previewData.typeOfBusiness || '-'}</p>
                                        <p><strong>Premium:</strong> ₹{formatINR(previewData.premiumSize)}</p>
                                        <p><strong>Follow-up:</strong> {formatDisplayDate(previewData.firstFollowUpDate)}</p>
                                        <p><strong>Renewal Date:</strong> {formatDisplayDate(previewData.tentativeStartDate)}</p>
                                        <p><strong>Email:</strong> {previewData.email || previewData.emailId || '-'}</p>
                                        <p><strong>Status:</strong> {getProspectStatus(previewData, showDisabled)}</p>
                                        <p className="full"><strong>Remarks:</strong> {previewData.remarks || '-'}</p>
                                        {showDisabled && (
                                            <p className="full"><strong>Lost Reason:</strong> {previewData.disabledReason || '-'}</p>
                                        )}
                                    </div>

                                    <div className="preview-actions">
                                        {canEdit && !showDisabled && (
                                            <button type="button" className="btn-primary" onClick={() => {
                                                setEditData(previewData);
                                                setPreviewData(null);
                                            }}>
                                                Edit Prospect
                                            </button>
                                        )}
                                        {canConvert && !showDisabled && (
                                            <button type="button" className="btn-secondary" onClick={() => {
                                                setConvertData(previewData);
                                                setPreviewData(null);
                                            }}>
                                                Convert
                                            </button>
                                        )}
                                    </div>
                                </aside>
                            </div>
                        )}

                        {convertData && (
                            <div className="edit-modal-overlay prospect-action-modal-overlay" onClick={() => setConvertData(null)}>
                                <div className="prospect-action-modal prospect-action-modal-convert" onClick={e => e.stopPropagation()}>
                                    <div className="prospect-action-modal-head">
                                        <span className="prospect-action-modal-icon convert">
                                            <FaSyncAlt />
                                        </span>
                                        <div>
                                            <span className="prospect-action-modal-kicker">Convert Prospect</span>
                                            <h3>Convert to policy?</h3>
                                            <p>Create a new corporate policy draft using this prospect's existing details.</p>
                                        </div>
                                    </div>

                                    <div className="prospect-action-modal-summary">
                                        <div>
                                            <span>Company</span>
                                            <strong>{convertData.prospectName || '-'}</strong>
                                        </div>
                                        <div>
                                            <span>Policy</span>
                                            <strong>{convertData.policyName || '-'}</strong>
                                        </div>
                                        <div>
                                            <span>Premium</span>
                                            <strong>₹{formatINR(convertData.premiumSize) || '-'}</strong>
                                        </div>
                                    </div>

                                    <div className="prospect-action-modal-note">
                                        You can review and complete all remaining corporate policy fields before final submission.
                                    </div>

                                    <div className="prospect-action-modal-actions">
                                        <button
                                            className="btn-primary prospect-action-primary"
                                            onClick={() => {
                                                navigate('/add-policy', {
                                                    state: {
                                                        fromProspect: true,
                                                        prospectData: {
                                                            ...convertData,
                                                            clientType: 'Corporate'
                                                        }
                                                    }
                                                });
                                            }}
                                        >
                                            <FaSyncAlt />
                                            Yes, Convert
                                        </button>
                                        <button className="btn-secondary prospect-action-secondary" onClick={() => setConvertData(null)}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {disableId && (
                            <div className="edit-modal-overlay prospect-action-modal-overlay" onClick={() => setDisableId(null)}>
                                <div className="prospect-action-modal prospect-action-modal-lost" onClick={e => e.stopPropagation()}>
                                    <div className="prospect-action-modal-head">
                                        <span className="prospect-action-modal-icon lost">
                                            <FaTimesCircle />
                                        </span>
                                        <div>
                                            <span className="prospect-action-modal-kicker">Mark Lost</span>
                                            <h3>Move prospect to lost?</h3>
                                            <p>This keeps the corporate record available in Lost Prospects with the reason captured.</p>
                                        </div>
                                    </div>

                                    <div className="prospect-action-modal-field">
                                        <label>Reason for lost <span>*</span></label>
                                        <textarea
                                            value={disableReason}
                                            onChange={e => setDisableReason(e.target.value)}
                                            placeholder="Example: Client chose another insurer, pricing mismatch, or no response..."
                                            required
                                        />
                                        <small>{disableReason.trim().length}/250 characters</small>
                                    </div>
                                    <div className="prospect-action-modal-actions">
                                        <button
                                            className="btn-danger prospect-action-danger"
                                            onClick={async () => {
                                                if (!disableReason.trim()) {
                                                    alert('Reason is required');
                                                    return;
                                                }

                                                await api.put(`${API_BASE_URL}/api/manage-prospects/disable/${disableId}`, {
                                                    reason: disableReason
                                                });

                                                setDisableId(null);
                                                setDisableReason('');
                                                loadProspects();
                                            }}
                                        >
                                            <FaTimesCircle />
                                            Confirm Lost
                                        </button>
                                        <button className="btn-secondary prospect-action-secondary" onClick={() => setDisableId(null)}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {editData && (
                            <div className="edit-modal-overlay">
                                <div className="edit-modal">
                                    <h3>Edit Corporate Prospect</h3>

                                    <div className="edit-modal-body">
                                        <div className="modal-field">
                                            <label>Company Name</label>
                                            <input value={editData.prospectName} className="read-only-input" />
                                        </div>

                                        {editData.classOfPolicyName && (
                                            <div className="modal-field">
                                                <label>Class of Policy</label>
                                                <input type="text" value={editData.classOfPolicyName} readOnly className="read-only-input" />
                                            </div>
                                        )}

                                        <div className="modal-field">
                                            <label>Policy Name</label>
                                            <input type="text" value={editData.policyName} readOnly className="read-only-input" />
                                        </div>

                                        <div className="modal-field">
                                            <label>Type of Business</label>
                                            <input
                                                type="text"
                                                value={
                                                    editData.typeOfBusiness === 'NEW'
                                                        ? 'New'
                                                        : editData.typeOfBusiness === 'RENEWAL'
                                                            ? 'I-Pro Renewal'
                                                            : 'Other Renewal'
                                                }
                                                readOnly
                                                className="read-only-input"
                                            />
                                        </div>

                                        <div className="modal-field">
                                            <label>Previous Policy Number</label>

                                            {editData.typeOfBusiness === 'RENEWAL' ? (
                                                <select
                                                    value={editData.previousPolicyNumber || ''}
                                                    onChange={e => {
                                                        const value = e.target.value.toUpperCase();
                                                        const errorMsg = validateEditPreviousPolicyNumber(value, editData.typeOfBusiness);

                                                        setEditData({
                                                            ...editData,
                                                            previousPolicyNumber: value
                                                        });

                                                        setEditErrors(prev => ({
                                                            ...prev,
                                                            previousPolicyNumber: errorMsg
                                                        }));
                                                    }}
                                                    className={editErrors.previousPolicyNumber ? 'input-error' : ''}
                                                >
                                                    <option value="">Select Previous Policy</option>
                                                    {previousPolicies.map(p => (
                                                        <option key={p._id} value={p.policyNumber}>
                                                            {p.policyNumber}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type="text"
                                                    placeholder="Previous policy number (if any)"
                                                    value={editData.previousPolicyNumber || ''}
                                                    onChange={e => {
                                                        const value = e.target.value.toUpperCase();
                                                        const errorMsg = validateEditPreviousPolicyNumber(value, editData.typeOfBusiness);

                                                        setEditData({
                                                            ...editData,
                                                            previousPolicyNumber: value
                                                        });

                                                        setEditErrors(prev => ({
                                                            ...prev,
                                                            previousPolicyNumber: errorMsg
                                                        }));
                                                    }}
                                                    className={editErrors.previousPolicyNumber ? 'input-error' : ''}
                                                />
                                            )}

                                            {editErrors.previousPolicyNumber && (
                                                <span className="field-error">{editErrors.previousPolicyNumber}</span>
                                            )}
                                        </div>

                                        <div className="modal-field">
                                            <label>Premium Amount</label>

                                            <div style={{ position: 'relative' }}>
                                                <span style={{ position: 'absolute', left: 10, top: '50%' }}>₹</span>
                                                <input
                                                    type="text"
                                                    value={premiumDisplay}
                                                    onChange={e => {
                                                        const raw = parseINR(e.target.value);

                                                        if (!raw) {
                                                            setPremiumDisplay('');
                                                            setPremiumError('');
                                                            setEditData(prev => ({ ...prev, premiumSize: '' }));
                                                            return;
                                                        }

                                                        const num = Number(raw);
                                                        if (num > 100000000) {
                                                            setPremiumError('Maximum allowed premium is ₹10,00,00,000');
                                                            return;
                                                        }

                                                        setPremiumError('');
                                                        setPremiumDisplay(formatINR(num));
                                                        setEditData(prev => ({ ...prev, premiumSize: num }));
                                                    }}
                                                />
                                            </div>

                                            {premiumError && <small className="field-error">{premiumError}</small>}
                                        </div>

                                        <div className="modal-field">
                                            <label>Renewal Date</label>
                                            <input
                                                type="date"
                                                value={formatDateForInput(editData.tentativeStartDate)}
                                                onChange={e =>
                                                    setEditData({
                                                        ...editData,
                                                        tentativeStartDate: e.target.value
                                                    })
                                                }
                                            />
                                        </div>

                                        <div className="modal-field">
                                            <label>First Follow-up Date</label>
                                            <input
                                                type="date"
                                                value={formatDateForInput(editData.firstFollowUpDate)}
                                                onChange={e =>
                                                    setEditData({
                                                        ...editData,
                                                        firstFollowUpDate: e.target.value
                                                    })
                                                }
                                            />
                                        </div>

                                        <div className="modal-field">
                                            <label>Remarks</label>
                                            <textarea
                                                value={editData.remarks || ''}
                                                onChange={e =>
                                                    setEditData({ ...editData, remarks: e.target.value })
                                                }
                                            />
                                        </div>
                                    </div>

                                    <div className="modal-actions">
                                        <button className="btn-primary" onClick={handleUpdate} disabled={isEditFormInvalid}>
                                            Save
                                        </button>
                                        <button className="btn-secondary" onClick={() => setEditData(null)}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {bulkLostModal.open && (
                            <div className="edit-modal-overlay" onClick={() => setBulkLostModal({ open: false, reason: '' })}>
                                <div className="edit-modal" onClick={e => e.stopPropagation()} style={{ width: 520 }}>
                                    <h3>Mark Selected Prospects as Lost</h3>
                                    <div className="modal-field">
                                        <label>Reason for Lost *</label>
                                        <textarea
                                            value={bulkLostModal.reason}
                                            onChange={e => setBulkLostModal(prev => ({ ...prev, reason: e.target.value }))}
                                            placeholder="Enter reason for all selected prospects..."
                                            required
                                        />
                                    </div>
                                    <div className="modal-actions">
                                        <button
                                            className="btn-danger"
                                            onClick={async () => {
                                                const reason = String(bulkLostModal.reason || '').trim();
                                                if (!reason) {
                                                    alert('Reason is required');
                                                    return;
                                                }

                                                const ids = [...new Set(selectedProspectIds)];
                                                if (!ids.length) {
                                                    alert('No prospects selected');
                                                    return;
                                                }

                                                for (const id of ids) {
                                                    await api.put(`${API_BASE_URL}/api/manage-prospects/disable/${id}`, { reason });
                                                }

                                                setBulkLostModal({ open: false, reason: '' });
                                                setSelectedProspectIds([]);
                                                loadProspects();
                                            }}
                                        >
                                            Confirm Lost ({selectedProspectIds.length})
                                        </button>
                                        <button className="btn-secondary" onClick={() => setBulkLostModal({ open: false, reason: '' })}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
};

export default CorporateProspectManage;
